import { Checkbox, TextField, Typography } from "@material-ui/core";
import React, { useMemo, useState } from "react";
import theme from "../../../assets/theme";
import CustomButton from "../../../components/buttons/CustomButton";
import { CustomIcon } from "../../../components/buttons/CustomIcon";
import { CustomButtonProps } from "../../../components/buttons/types/CustomButtonTypes";
import { FlexContainer } from "../../../components/containers";
import { useLangLabels } from "../../../shared/lang/services/useLangLabels";

export type PasswordFieldsMode = "new" | "edit";

interface Props {
  mode?: PasswordFieldsMode;
  /** Receives the password once it passes every strength/match rule. */
  onSave?: (password: string) => void;
  /** Drives the save button's spinner. */
  saveStatus?: CustomButtonProps["status"];
  /** Defaults to the generic "Save" label. */
  saveLabel?: string;
  /** Merged over the button's full-width default. */
  saveStyleProps?: CustomButtonProps["styleProps"];
  saveDisabled?: boolean;
  /** Rendered between the inputs and the save button. */
  children?: React.ReactNode;
}

const PENDING_COLOR = "#9e9e9e";

const PasswordFields: React.FC<Props> = ({
  mode = "edit",
  onSave,
  saveStatus,
  saveLabel,
  saveStyleProps,
  saveDisabled,
  children,
}) => {
  const { lang } = useLangLabels();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const checks = useMemo(() => {
    const strength = lang.sidebar.config.passwordStrength;
    return [
      { label: strength.minChars, valid: password.length >= 8 },
      { label: strength.hasUppercase, valid: /[A-Z]/.test(password) },
      { label: strength.hasLowercase, valid: /[a-z]/.test(password) },
      { label: strength.hasNumber, valid: /[0-9]/.test(password) },
      { label: strength.hasSpecialChar, valid: /[^A-Za-z0-9]/.test(password) },
      {
        label: strength.passwordsMatch,
        valid: password.length > 0 && password === confirmPassword,
      },
    ];
  }, [password, confirmPassword, lang]);

  const isValid = checks.every(check => check.valid);

  const save = () => {
    if (isValid && !saveDisabled) onSave?.(password);
  };

  return (
    <FlexContainer flexDirection="column" gap="16px" width="100%">
      <TextField
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        color="secondary"
        label={
          mode === "new" ? lang.sidebar.config.currentPassword : lang.sidebar.config.newPassword
        }
      />

      <FlexContainer flexDirection="column" gap="4px">
        {checks.map(check => (
          <FlexContainer key={check.label} align="center" gap="4px">
            <CustomIcon
              icon="check"
              style={{
                fontSize: "16px",
                margin: 0,
                color: check.valid ? theme.palette.success.main : PENDING_COLOR,
              }}
            />
            <Typography
              variant="body2"
              style={{
                color: check.valid ? theme.palette.success.main : PENDING_COLOR,
              }}
            >
              {check.label}
            </Typography>
          </FlexContainer>
        ))}
      </FlexContainer>

      <TextField
        type={showPassword ? "text" : "password"}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        color="secondary"
        label={lang.sidebar.config.repeatPassword}
      />

      <FlexContainer align="center">
        <Checkbox
          size="small"
          style={{ padding: "0 4px 0 0" }}
          color="secondary"
          checked={showPassword}
          onChange={() => setShowPassword(!showPassword)}
        />
        <Typography variant="body2">{lang.sidebar.config.showPassword}</Typography>
      </FlexContainer>

      {children}

      <CustomButton
        color="secondary"
        status={saveStatus}
        disabled={!isValid || saveDisabled}
        action={save}
        styleProps={{ width: "100%", ...saveStyleProps }}
      >
        {saveLabel || lang.crudActions.save}
      </CustomButton>
    </FlexContainer>
  );
};

export default PasswordFields;
